import {useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {BarChart3, RefreshCw} from 'lucide-react';
import {
  Alert,
  Button,
  AlertDescription,
  AlertTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {usePrefetchReportToggles, useReport, useReports} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {type ReportParameter, type ReportQuery, type ReportSummary} from '@/api/reporting';
import {ReportParameters, parameterDefaults} from '@/components/reporting/ReportParameters';
import {ReportRenderer} from '@/components/reporting/ReportRenderer';
import {ReportsHome} from '@/components/reporting/ReportsHome';

const GENERATED_AT = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short'
});

// Report parameters live in the URL, so a range someone is looking at can be pasted to a
// colleague and reloaded. Empty values are dropped rather than sent: the service then applies the
// report's own default.
function queryFrom(searchParams: URLSearchParams): ReportQuery {
  return Object.fromEntries([...searchParams.entries()].filter(([, value]) => value !== ''));
}

// The title, its Re-run report button and the parameter inputs. The parameters as edited but not yet
// run are held here as a draft; the URL holds what was last run, so the draft is seeded from it (over
// the report's defaults, which the service applies to anything the URL leaves out) and the page
// re-keys this whenever the report or the run parameters change underneath.
function ReportControls({
  summary,
  parameters,
  applied,
  running,
  onRun
}: {
  readonly summary: ReportSummary;
  // The report's parameters as the last response gave them, with each select's choices; the list's
  // own do not carry those. Until a report has loaded they are the list's.
  readonly parameters: readonly ReportParameter[];
  readonly applied: ReportQuery;
  readonly running: boolean;
  readonly onRun: (next: ReportQuery) => void;
}) {
  const [draft, setDraft] = useState<ReportQuery>(() => ({
    ...parameterDefaults(parameters),
    ...applied
  }));
  // Edits not yet run: the draft against what was last run (the URL over the defaults), so the
  // analyst is told the rows on screen no longer match the filters.
  const lastRun = {...parameterDefaults(parameters), ...applied};
  const stale = [...Object.keys(draft), ...Object.keys(lastRun)].some(
    (name) => (draft[name] ?? '') !== (lastRun[name] ?? '')
  );
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-medium tracking-tight">{summary.title}</h2>
          {summary.description ? (
            <p className="text-muted-foreground text-sm">{summary.description}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {stale ? (
            <p role="status" className="text-muted-foreground text-xs">
              Filters changed. Re-run the report to apply them.
            </p>
          ) : null}
          <Button
            type="button"
            variant={stale ? 'default' : 'outline'}
            size="sm"
            disabled={running}
            onClick={() => {
              onRun(draft);
            }}>
            {running ? <Spinner /> : <RefreshCw />}
            Re-run report
          </Button>
        </div>
      </div>
      <ReportParameters
        parameters={parameters}
        values={draft}
        onChange={(next) => {
          // A toggle switches the view rather than refining it, so it runs at once (its other
          // options are prefetched); every other edit waits for Re-run report.
          const switched = parameters.some(
            (parameter) =>
              parameter.type === 'toggle' && next[parameter.name] !== draft[parameter.name]
          );
          setDraft(next);
          if (switched) {
            onRun(next);
          }
        }}
      />
    </>
  );
}

// A wait the page cannot show anything for yet: the design system's empty state, with the spinner
// as its media.
function LoadingState({title}: {readonly title: string}) {
  return (
    <Empty className="py-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Spinner />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
      </EmptyHeader>
    </Empty>
  );
}

export function ReportingPage() {
  const {reportId} = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reports = useReports();
  const list = reports.data ?? [];

  const summary = list.find((report) => report.id === reportId);
  const query = queryFrom(searchParams);
  const report = useReport(summary?.id, query);
  const reportParameters = report.data?.parameters;
  usePrefetchReportToggles(summary?.id, reportParameters ?? [], {
    ...parameterDefaults(reportParameters ?? []),
    ...query
  });

  if (reports.isLoading) {
    return <LoadingState title="Loading reports…" />;
  }

  if (reports.isError) {
    return (
      <div className="mx-auto max-w-screen-2xl px-4 py-6">
        <Alert variant="destructive">
          <AlertTitle>Reports unavailable</AlertTitle>
          <AlertDescription>{getApiErrorMessage(reports.error)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (list.length === 0 || (reportId !== undefined && summary === undefined)) {
    return (
      <Empty className="py-16">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <BarChart3 />
          </EmptyMedia>
          <EmptyTitle>{list.length === 0 ? 'No reports yet' : 'No such report'}</EmptyTitle>
          <EmptyDescription>
            {list.length === 0
              ? 'Nothing has been published for this environment.'
              : 'That report is not one you can see here.'}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  // /reporting on its own is the reports homepage: every report, as a card that opens it.
  if (summary === undefined) {
    return <ReportsHome reports={list} />;
  }

  // Switching tab keeps the parameters the next report also declares, so a date range carries over.
  const openReport = (id: string) => {
    const declared = new Set((list.find((r) => r.id === id)?.parameters ?? []).map((p) => p.name));
    const carried = new URLSearchParams(
      Object.entries(query).filter(([name]) => declared.has(name))
    ).toString();
    void navigate(`/reporting/${id}${carried ? `?${carried}` : ''}`);
  };

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="mx-auto max-w-screen-2xl space-y-6 px-4 py-6">
        {list.length > 1 ? (
          <Tabs
            value={summary.id}
            onValueChange={(value) => {
              openReport(String(value));
            }}>
            <TabsList aria-label="Report">
              {list.map((option) => (
                <TabsTrigger key={option.id} value={option.id}>
                  {option.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : null}

        <ReportControls
          key={`${summary.id}?${JSON.stringify(query)}`}
          summary={summary}
          parameters={report.data?.parameters ?? summary.parameters ?? []}
          applied={query}
          running={report.isFetching}
          onRun={(next) => {
            const nonEmpty = Object.entries(next).filter(([, value]) => value !== '');
            // Re-running unchanged parameters is a deliberate refresh, which a URL no-op would swallow.
            if (JSON.stringify(nonEmpty) === JSON.stringify(Object.entries(query))) {
              void report.refetch();
            } else {
              setSearchParams(nonEmpty, {replace: true});
            }
          }}
        />

        {report.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Report failed</AlertTitle>
            <AlertDescription>{getApiErrorMessage(report.error)}</AlertDescription>
          </Alert>
        ) : null}

        {report.data ? (
          <>
            <div
              aria-busy={report.isFetching}
              className={report.isFetching ? 'opacity-60 transition-opacity' : undefined}>
              <ReportRenderer report={report.data} />
            </div>
            <p className="text-muted-foreground text-xs">
              Generated {GENERATED_AT.format(new Date(report.data.generated_at))} for{' '}
              {Object.entries(report.data.parameter_values)
                .map(([name, value]) => `${name} ${value}`)
                .join(', ')}
            </p>
          </>
        ) : report.isLoading ? (
          <LoadingState title="Running report…" />
        ) : null}
      </div>
    </div>
  );
}
